import { ref } from 'vue'

const DEFAULT_PER_PAGE = 20

/**
 * Server-driven pagination bookkeeping shared by the report screens and the
 * admin directory panels: the `pagination` state a paginated endpoint's
 * response carries (`page`, `per_page`, `total`, `total_pages`), a page
 * reset when filters change, a reset to a blank first page when a load
 * fails, and a clamp back to the last page when the requested page no
 * longer exists (e.g. revoking the last rows on the final page).
 *
 * Usage:
 *   const { pagination, setPage, resetPage, applyResponse, resetOnError } =
 *       useServerPagination({ perPage: 25 })
 *
 *   changePage(page) { this.setPage(page); this.load() }
 *   applyFilters() { this.resetPage(); this.load() }
 *   // in the load success handler:
 *   if (this.applyResponse(response.data.pagination)) this.load()
 *   // in the load error handler:
 *   this.resetOnError()
 *   // after one record is added to or removed from the list in place:
 *   this.shiftTotal(1) // or -1
 *
 * @param {{perPage?: number}} options
 */
export function useServerPagination({ perPage = DEFAULT_PER_PAGE } = {}) {
    function blankPagination() {
        return { page: 1, per_page: perPage, total: 0, total_pages: 0 }
    }

    const pagination = ref(blankPagination())

    function setPage(page) {
        pagination.value.page = page
    }

    function resetPage() {
        pagination.value.page = 1
    }

    /**
     * Applies the `pagination` block from a paginated endpoint's response
     * (or resets to a blank first page if the endpoint omitted it), then
     * clamps back to the last page when the requested page no longer
     * exists. Returns true when it clamped, so the caller knows to reload
     * at the corrected page.
     */
    function applyResponse(responsePagination) {
        // Merged rather than replaced outright, so a response whose
        // pagination block omits a field (e.g. `per_page`) can't leave the
        // state missing it for the next request.
        pagination.value = responsePagination
            ? { ...blankPagination(), ...responsePagination }
            : blankPagination()

        const { page, total, total_pages: totalPages } = pagination.value
        if (total > 0 && page > totalPages) {
            pagination.value.page = totalPages
            return true
        }

        return false
    }

    /**
     * Moves the total by `delta` after one record is added to or removed from
     * the list in place, and recomputes `total_pages` to match, so the pager
     * stays right without a reload. The total never goes below zero.
     *
     * @param {number} delta
     */
    function shiftTotal(delta) {
        const total = Math.max(0, pagination.value.total + delta)

        pagination.value = {
            ...pagination.value,
            total,
            total_pages: Math.ceil(total / pagination.value.per_page),
        }
    }

    function resetOnError() {
        pagination.value = blankPagination()
    }

    return {
        pagination,
        setPage,
        resetPage,
        applyResponse,
        shiftTotal,
        resetOnError,
    }
}
